/**
 * X handles next to wallets (opt-in, linked through Privy). Lookups go through the batch route,
 * GET /api/identity?wallets=a,b,c, which answers 200 with the linked wallets only (an unlinked wallet is not an
 * error), one request per list and never one per row. Results are cached for 10 minutes, misses included.
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from './client';
import type { WalletIdentity } from './types';

export const identityKey = (wallet: string) => ['identity', wallet] as const;

/**
 * Set once the batch route answers 404 (a server without it): no handles are shown for the rest of the session.
 * Asking GET /api/identity/:wallet instead would log a 404 for every wallet without a link.
 */
let batchMissing = false;

async function lookup(wallets: string[]): Promise<Record<string, WalletIdentity>> {
  if (batchMissing) return {};
  try {
    const { items } = await api.identities(wallets);
    return Object.fromEntries(items.map((i) => [i.wallet, i]));
  } catch (e) {
    if (!(e instanceof ApiError && e.status === 404)) throw e;
    batchMissing = true;
    return {};
  }
}

const retry = (n: number, e: unknown) => !(e instanceof ApiError && e.status > 0 && e.status < 500) && n < 1;

/** The wallet's X profile, or null when it has none (profile header, coin creator, the user's own menu). */
export function useIdentity(wallet: string | null | undefined) {
  return useQuery({
    queryKey: identityKey(wallet ?? ''),
    enabled: !!wallet,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    retry,
    queryFn: async (): Promise<WalletIdentity | null> => (await lookup([wallet!]))[wallet!] ?? null,
  });
}

/** Handles for a list (trades, holders): at most 100 wallets per request. */
export function useIdentities(wallets: string[]) {
  const uniq = [...new Set(wallets.filter(Boolean))].sort().slice(0, 100);
  return useQuery({
    queryKey: ['identities', uniq.join(',')],
    enabled: uniq.length > 0,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    retry,
    queryFn: () => lookup(uniq),
  });
}

export const xUrl = (username: string) => `https://x.com/${encodeURIComponent(username)}`;
